/**
 * buildMuiTheme.js — o ÚNICO lugar que monta o tema MUI do painel.
 *
 * Usado pelo Admin (components/ThemeApplier.jsx) e pelo estúdio do pacote do Claude Design
 * (design/pacote/estudio/tema.js — o build:design empacota este arquivo). Uma função só: a tela que
 * o CD valida no estúdio recebe os mesmos padrões de componente (cantos, sombras, hover, anel de
 * foco) e a mesma tipografia que o cliente vê no Admin.
 *
 * Entrada = as CSS vars já resolvidas do modo: escala operacional (`--bg-page`, `--surface`,
 * `--shadow-card`…) + acento do nicho (`--color-primary`, `--on-primary`, `--color-primary-soft`…).
 * O Admin passa `{ ...themeTokens.scale, ...themeTokens.cssVars }`; o estúdio, a escala de
 * `src/modes/operational.js` + o `accent[modo]` de `src/themes/<id>.js`.
 *
 * Pura: sem React, sem DOM.
 */
import { createTheme } from '@mui/material/styles';
import { muiPalette, shape } from './designTokens.js';
import { muiThemeConfig } from './muiThemeConfig.js';

const TRANSITION = 'all 0.2s ease-in-out';

// Tema base (modo claro + acento padrão): é dele que saem as partes do MUI que o painel não define.
const base = createTheme({ palette: muiPalette });

// Corpo FIXO (D19) em TODAS as variantes. A tipografia do tema base já vem calculada, com a lista do MUI
// ("Roboto", "Helvetica", "Arial") gravada em cada variante — e o painel não baixa Roboto, então até 28/10/2026 o
// texto do MUI saía em Arial no Windows. Troca só a família: o espaçamento entre letras de cada variante fica
// como estava (com `typography: { fontFamily }` o MUI o zeraria, e mudaria também o texto que já era DM Sans).
// h1/h2 ganham a fonte do nicho no muiThemeConfig; a variante `inherit` continua herdando do pai.
const BODY_FONT = 'var(--font-family)';
const typography = Object.fromEntries(Object.entries(base.typography).map(([key, value]) => [
  key,
  value?.fontFamily === base.typography.fontFamily ? { ...value, fontFamily: BODY_FONT } : value,
]));
typography.fontFamily = BODY_FONT;

/**
 * @param {{ mode: 'light'|'dark', vars: Object<string, string> }} input
 * @returns {import('@mui/material/styles').Theme}
 */
export function buildMuiTheme({ mode, vars }) {
  return createTheme({
    ...base,
    palette: {
      ...base.palette,
      mode,
      primary: { main: vars['--color-primary'], dark: vars['--color-primary-dark'], light: vars['--color-primary-light'], contrastText: vars['--on-primary'] },
      secondary: { main: vars['--color-secondary'] },
      background: { default: vars['--bg-page'], paper: vars['--surface'] },
      text: { primary: vars['--text-primary'], secondary: vars['--text-secondary'], disabled: vars['--text-disabled'] },
      success: { main: vars['--color-success'] },
      error: { main: vars['--color-error'] },
      warning: { main: vars['--color-warning'] },
      info: { main: vars['--color-info'] },
      divider: vars['--divider'],
    },
    typography,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusCard,
            boxShadow: vars['--shadow-card'],
            transition: TRANSITION,
            '&:hover': {
              boxShadow: vars['--shadow-hover'],
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusCard,
            // MUI escurece o Paper no modo escuro com um gradiente de "elevação"; a superfície
            // do painel é a da escala, sem véu por cima.
            backgroundImage: 'none',
          },
          elevation1: { boxShadow: vars['--shadow-card'] },
          elevation2: { boxShadow: vars['--shadow-card'] },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusSm,
            textTransform: 'none',
            fontWeight: 500,
            transition: TRANSITION,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: vars['--shadow-card'],
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: vars['--shadow-card'],
            '&:hover': { boxShadow: vars['--shadow-hover'] },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: shape.radiusSm },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: shape.radiusLg,
            boxShadow: vars['--shadow-hover'],
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: shape.radiusSm,
              transition: TRANSITION,
              '&:hover': { boxShadow: `0 0 0 2px ${vars['--color-primary-soft']}` },
              '&.Mui-focused': { boxShadow: `0 0 0 2px ${vars['--color-primary-soft']}` },
            },
          },
        },
      },
    },
  }, muiThemeConfig); // deep-merge do ambiente pinado (escala tipográfica, MuiSwitch, densidade)
}
